import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Users, AlertCircle, Clock, Phone, Mail, ChevronRight, Activity, TrendingDown } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

const reasonLabel = (r) => r === "excused" ? "Me arsye" : r === "unexcused" ? "Pa arsye" : r;
const reasonColor = (r) => r === "excused" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-terracotta/10 text-terracotta border-terracotta/30";

export default function SecretaryDashboard() {
    const [data, setData] = useState({ classes: [], feed: [], totals: { students: 0, present: 0, absent: 0 }, date: todayISO() });
    const [loading, setLoading] = useState(true);
    const [pulse, setPulse] = useState(0); // increments each refresh — used for visual "live" indicator

    const refresh = async () => {
        try {
            const r = await api.get("/secretary/dashboard");
            setData(r.data);
            setPulse((p) => p + 1);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 8000); // poll every 8s for live updates
        return () => clearInterval(t);
    }, []);

    const callTel = (phone) => phone ? `tel:${phone.replace(/\s/g, "")}` : null;
    const mailto = (email) => email ? `mailto:${email}` : null;

    return (
        <Layout>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-2">
                        Sekretaria
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${pulse > 0 ? "bg-sage/15 text-sage" : "bg-slate-100 text-slate-500"} transition`}>
                            <Activity className={`w-3 h-3 ${pulse > 0 ? "animate-pulse" : ""}`} /> KOHË REALE
                        </span>
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Mungesat e Ditës</h1>
                    <p className="text-slate-600 mt-2 text-sm">Përditësohet automatikisht çdo 8 sekonda • {data.date}</p>
                </div>
                <Link to="/secretary/staff" className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-slate-200 bg-white hover:border-oxford text-sm font-semibold" data-testid="secretary-staff-link">
                    <Users className="w-4 h-4" /> Stafi
                </Link>
            </div>

            {/* Top totals */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total nxënës</div>
                    <div className="font-heading text-3xl font-black text-oxford mt-1" data-testid="total-students">{data.totals.students}</div>
                </div>
                <div className="bg-sage/5 border border-sage/30 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-sage font-bold">Prezent sot</div>
                    <div className="font-heading text-3xl font-black text-sage mt-1" data-testid="total-present">{data.totals.present}</div>
                </div>
                <div className="bg-terracotta/10 border border-terracotta/30 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-terracotta font-bold">Mungojnë</div>
                    <div className="font-heading text-3xl font-black text-terracotta mt-1" data-testid="total-absent">{data.totals.absent}</div>
                </div>
            </div>

            {/* Class cards grid */}
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-3">Klasat</h2>
            {loading ? (
                <div className="text-slate-500">Duke ngarkuar…</div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
                    {data.classes.filter((c) => c.student_count > 0).map((c) => {
                        const ratio = c.student_count > 0 ? c.absent_today / c.student_count : 0;
                        const accent = ratio === 0 ? "border-sage/40 bg-sage/5" : ratio < 0.2 ? "border-amber-200 bg-amber-50" : "border-terracotta/40 bg-terracotta/10";
                        return (
                            <Link key={c.id} to={`/secretary/class/${c.id}`} className={`block bg-white border-2 rounded-2xl p-4 hover:border-oxford transition group ${accent}`} data-testid={`class-card-${c.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-heading font-black text-slate-900 group-hover:text-oxford">{c.name}</div>
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <div className={`font-heading text-2xl font-black ${c.absent_today > 0 ? "text-terracotta" : "text-sage"}`}>{c.absent_today}</div>
                                    <div className="text-xs text-slate-500">/ {c.student_count} mungojnë</div>
                                </div>
                                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-terracotta" style={{ width: `${Math.min(ratio * 100, 100)}%` }} />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* Live feed */}
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                Mungesat aktive
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{data.feed.length}</span>
            </h2>
            {data.feed.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <Activity className="w-10 h-10 text-sage mx-auto mb-3" />
                    <div className="font-heading text-xl font-bold text-slate-800">Asnjë mungesë e raportuar</div>
                    <p className="text-sm text-slate-600 mt-1">Të gjithë nxënësit janë në klasë sot. Mësuesit do të raportojnë në kohë reale.</p>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="divide-y divide-slate-200">
                        {data.feed.map((a) => (
                            <div key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 hover:bg-bone/40" data-testid={`feed-item-${a.id}`}>
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${a.reason === "unexcused" ? "bg-terracotta/10 text-terracotta" : "bg-amber-100 text-amber-700"}`}>
                                        <AlertCircle className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <Link to={`/secretary/class/${data.classes.find((c) => c.name === a.class_name)?.id || ""}`} className="font-bold text-slate-900 hover:text-oxford truncate block">{a.student_name}</Link>
                                        <div className="text-xs text-slate-500 truncate">
                                            {a.class_name} • {a.subject_name} •{" "}
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold ${reasonColor(a.reason)}`}>{reasonLabel(a.reason)}</span>
                                            {a.note && <span> — {a.note}</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 ml-13 sm:ml-0" data-testid={`feed-contacts-${a.id}`}>
                                    {a.parent_phone && (
                                        <a href={callTel(a.parent_phone)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sage/10 text-sage hover:bg-sage/20 text-xs font-semibold border border-sage/30" data-testid={`call-parent-${a.id}`}>
                                            <Phone className="w-3 h-3" /> {a.parent_phone}
                                        </a>
                                    )}
                                    {a.parent_email && (
                                        <a href={mailto(a.parent_email)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-oxford/10 text-oxford hover:bg-oxford/20 text-xs font-semibold border border-oxford/20" data-testid={`mail-parent-${a.id}`}>
                                            <Mail className="w-3 h-3" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Layout>
    );
}
